import { SortingState, Updater } from '@tanstack/react-table';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import {
  AgreementDTO,
  DirectionType,
  DirectoryDTO,
  InsuranceAreaDTO,
} from '@/types/api-types';
import {
  BreadcrumbItemType,
  ExplorerItem,
  NormalizedAgreement,
  NormalizedDirectory,
  NormalizedFile,
  NormalizedInsuranceArea,
  NormalizedInsuranceObject,
  NormalizedState,
} from '@/types/file-explorer-types';

import { getInsuranceObjectIdFromInsuranceAreaId } from '@/utils/get-agreement-id-from-insurance-area-id';

import {
  AGREEMENT_ROOT_NAME,
  INDEX_OBJECT_TYPES,
  ROOT_UUID,
} from '@/constants/global-constants';
import {
  COLUMNS_BY_TYPE_OF_EXPLORER_ITEM,
  ExplorerItemType,
} from '@/constants/table-columns-config';

import { useApiManagerStore } from './apiManagerStore';
import { useFiltersStore } from './filtersStore';
import { useGlobalStore } from './globalStore';
import { useSearchStore } from './searchStore';

interface FileExplorerStore {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  breadcrumbItems: BreadcrumbItemType[];
  directoriesState: NormalizedState<NormalizedDirectory>;
  agreementsState: NormalizedState<NormalizedAgreement>;
  filesState: NormalizedState<NormalizedFile>;
  insuranceAreaState: NormalizedState<NormalizedInsuranceArea>;
  insuranceObjectsState: NormalizedState<NormalizedInsuranceObject>;

  isRootLevel: boolean;
  setIsRootLevel: (isRootLevel: boolean) => void;

  // Data fetching methods
  getDirectoriesState: (
    skip?: number,
    take?: number,
  ) => Promise<NormalizedState<NormalizedDirectory>>;
  getAgreementsState: (options?: {
    parent_uuid?: InsuranceAreaDTO['uuid'];
    exclude_archived?: boolean;
  }) => Promise<NormalizedState<NormalizedAgreement>>;
  getAgreementsStateSearch: (
    agreementsIds: string[],
  ) => Promise<NormalizedState<NormalizedAgreement>>;
  setAgreementsState: (
    agreementsState: NormalizedState<NormalizedAgreement>,
  ) => void;

  updateAgreementInStateByDTO: (agreement: AgreementDTO) => void;

  setAgreementsUuids: (uuids: string[]) => void;

  addAgreement: (agreement: NormalizedAgreement) => void;
  getFilesState: (
    agreementUuid?: string,
    skip?: number,
    take?: number,
  ) => Promise<NormalizedState<NormalizedFile>>;
  setFilesState: (filesState: NormalizedState<NormalizedFile>) => void;

  getInsuranceObjectsState: (
    skip: number,
    take: number,
    parent_uuid?: string,
  ) => Promise<NormalizedState<NormalizedInsuranceObject>>;
  setInsuranceObjectsUuids: (uuids: string[]) => void;
  setInsuranceObjectsState: (
    insuranceObjectsState: NormalizedState<NormalizedInsuranceObject>,
  ) => void;
  getInsuranceAreaState: (
    skip: number,
    take: number,
    parent_uuid?: string,
  ) => Promise<NormalizedState<NormalizedInsuranceArea>>;
  setInsuranceAreaState: (
    insuranceAreaState: NormalizedState<NormalizedInsuranceArea>,
  ) => void;

  setDirectoriesState: (directories: DirectoryDTO[]) => void;

  // Helper methods
  getStateByType: (
    type: ExplorerItemType,
    parentUuid?: string,
    pagination?: { pageIndex: number; pageSize: number },
    exclude_archived?: boolean,
  ) => Promise<NormalizedState<ExplorerItem>>;
  getEntityByUuid: (
    type: ExplorerItemType,
    currentUUID: string,
  ) => Promise<ExplorerItem | null>;
  getBreadcrumbItems: (
    state_entityTypeLevel: ExplorerItemType,
    parentUuid: string,
  ) => Promise<BreadcrumbItemType[]>;

  // Filter state
  sorting: SortingState;
  visibleColumns: string[];

  // Media view mode (grid/list)
  mediaViewMode: 'grid' | 'list';
  setMediaViewMode: (mode: 'grid' | 'list') => void;

  // Filter and Sort actions
  setSorting: (updater: Updater<SortingState>) => void;
  setVisibleColumns: (columns: string[]) => void;
  clearFiltersAndSorting: () => void;

  setEntitiesCount: (entitiesCount: { DIRECTORY: number }) => void;

  // Clear files cache for a specific agreement
  clearFilesCache: (agreementUuid?: string) => void;

  // Remove a specific file from cache
  removeFileFromCache: (fileUuid: string) => void;
}

export const useFileExplorerStore = create<FileExplorerStore>()(
  persist(
    immer(
      (set, get): FileExplorerStore => ({
        // Initial state
        isLoading: false,
        setIsLoading: (isLoading: boolean) => set({ isLoading }),
        breadcrumbItems: [],
        directoriesState: {
          entities: {},
          rootIds: [],
          entityTypeLevel: ExplorerItemType.DIRECTORY,
          totalCount: 0,
        },
        insuranceObjectsState: {
          entities: {},
          rootIds: [],
          entityTypeLevel: ExplorerItemType.INSURANCE_OBJECT,
          totalCount: 0,
        },
        insuranceAreaState: {
          entities: {},
          rootIds: [],
          entityTypeLevel: ExplorerItemType.INSURANCE_AREA,
          totalCount: 2,
        },
        agreementsState: {
          entities: {},
          rootIds: [],
          entityTypeLevel: ExplorerItemType.AGREEMENT,
          totalCount: 50,
        },
        filesState: {
          entities: {},
          rootIds: [],
          entityTypeLevel: ExplorerItemType.FILE,
          totalCount: 0,
        },
        isRootLevel: true,
        setIsRootLevel: (isRootLevel: boolean) => set({ isRootLevel }),
        // Initial filter state
        sorting: [],
        visibleColumns: COLUMNS_BY_TYPE_OF_EXPLORER_ITEM[
          ExplorerItemType.AGREEMENT
        ].slice(0, 4),

        // Media view mode
        mediaViewMode: 'grid',
        setMediaViewMode: (mode) => set({ mediaViewMode: mode }),

        setEntitiesCount: (entitiesCount: { DIRECTORY: number }) => {
          set((state) => {
            state.directoriesState.totalCount = entitiesCount.DIRECTORY;
          });
        },

        setVisibleColumns: (columns: string[]) => {
          set((state) => {
            state.visibleColumns = columns;
          });
        },

        setFilesState: (filesState: NormalizedState<NormalizedFile>) => {
          set((state) => {
            state.filesState = filesState;
          });
        },

        setSorting: (updater) => {
          set((state) => {
            state.sorting =
              typeof updater === 'function' ? updater(state.sorting) : updater;
          });
        },

        clearFiltersAndSorting: () => {
          get().setSorting([]);
        },

        setDirectoriesState: (directories: DirectoryDTO[]) => {
          const { directoriesState } = get();
          const directoriesEntities = directories.reduce(
            (acc, dir) => {
              acc[dir.uuid] = {
                ...dir,
                parentId: null,
                breadcrumb_name: dir.abbreviation,
              };
              return acc;
            },
            {} as Record<string, NormalizedDirectory>,
          );

          const directoriesRootIds = [
            ...new Set(Object.keys(directoriesEntities)),
          ];

          const newDirectoriesState: NormalizedState<NormalizedDirectory> = {
            entities: directoriesEntities,
            rootIds: directoriesRootIds,
            entityTypeLevel: ExplorerItemType.DIRECTORY,
            totalCount: directoriesState.totalCount,
          };

          set((state) => {
            state.directoriesState = newDirectoriesState;
          });
        },

        // Data fetching methods using apiManagerStore
        getDirectoriesState: async (skip?: number, take?: number) => {
          const { directoriesState } = get();
          const { getDirectories } = useApiManagerStore.getState();
          const directories = await getDirectories(take, skip);

          const directoriesEntities = directories.reduce(
            (acc, dir) => {
              acc[dir.uuid] = {
                ...dir,
                parentId: null,
                breadcrumb_name: dir.abbreviation,
              };
              return acc;
            },
            {} as Record<string, NormalizedDirectory>,
          );

          const directoriesRootIds = [
            ...new Set(Object.keys(directoriesEntities)),
          ];

          const newDirectoriesState: NormalizedState<NormalizedDirectory> = {
            entities: directoriesEntities,
            rootIds: directoriesRootIds,
            entityTypeLevel: ExplorerItemType.DIRECTORY,
            totalCount: directoriesState.totalCount,
          };

          set((state) => {
            state.directoriesState = newDirectoriesState;
          });
          return newDirectoriesState;
        },

        getAgreementsState: async (options?: {
          parent_uuid?: InsuranceAreaDTO['uuid'];
          exclude_archived?: boolean;
        }) => {
          const { indexes, getAutoFetchUsers } = useGlobalStore.getState();
          const { getAgreements, getInsuranceObjects, directories } =
            useApiManagerStore.getState();

          const parent_uuid = options?.parent_uuid
            ? getInsuranceObjectIdFromInsuranceAreaId(options.parent_uuid)
            : undefined;
          const { pagination } = useFiltersStore.getState();

          let idsForFetch: string[] = [];
          if (!parent_uuid) {
            idsForFetch = await useSearchStore
              .getState()
              .searchAgreementUuidsForMyDocuments('dashboard');
            if (idsForFetch.length === 0) {
              // Update store
              const emptyAgreement = {
                totalCount: idsForFetch.length,
                entities: {},
                rootIds: idsForFetch,
                entityTypeLevel: ExplorerItemType.AGREEMENT,
              };
              set((state) => {
                state.agreementsState = emptyAgreement;
              });
              return emptyAgreement;
            }
          }

          let fetchedAgreements =
            idsForFetch.length > 0
              ? await getAgreements(idsForFetch, {
                  exclude_archived: options?.exclude_archived || false,
                })
              : await getAgreements(undefined, {
                  take: pagination?.pageSize || 20,
                  skip:
                    (pagination?.pageIndex || 0) * (pagination?.pageSize || 20),
                  parent_uuid: parent_uuid || undefined,
                  exclude_archived: options?.exclude_archived || false,
                });

          // Preserve ES order when IDs are provided
          if (idsForFetch.length > 0) {
            const orderIndex: Record<string, number> = {};
            idsForFetch.forEach((id, i) => (orderIndex[id] = i));
            fetchedAgreements.sort((a, b) => {
              const ai = orderIndex[a.uuid];
              const bi = orderIndex[b.uuid];
              const da = ai === undefined ? Number.POSITIVE_INFINITY : ai;
              const db = bi === undefined ? Number.POSITIVE_INFINITY : bi;
              return da - db;
            });
          }

          // Extract UUIDs for parallel fetching
          const userUuids = [
            ...new Set(
              fetchedAgreements
                .flatMap((agreement) => [agreement.assignee, agreement.creator])
                .filter((uuid): uuid is string => uuid !== undefined),
            ),
          ];

          const insuranceObjectsUuids = [
            ...new Set(
              fetchedAgreements.flatMap((agreement) => [
                agreement.insurance_object,
              ]),
            ),
          ];

          // Parallel requests for all required data
          const [indexesData, insuranceObjects] = await Promise.all([
            Promise.resolve(indexes), // Use cached indexes from global store
            getInsuranceObjects(insuranceObjectsUuids),
            getAutoFetchUsers(userUuids),
          ]);

          // Create normalized agreements in preserved order
          const agreementsEntities: Record<string, NormalizedAgreement> = {};
          const rootIds: string[] = [];
          for (const agreement of fetchedAgreements) {
            const indexOfAgreement = indexesData.find(
              (index) => index.uuid === agreement.index,
            );
            if (!indexOfAgreement) continue;

            const insuranceObject = insuranceObjects.find(
              (obj) => obj.uuid === agreement.insurance_object,
            );

            const parentId =
              `${indexOfAgreement.insurance_area}_${agreement.insurance_object}` as `${DirectionType}_${string}`;
            // If parent_uuid is provided, filter out agreements that don't match the parent_uuid HERE
            if (!!options?.parent_uuid && parentId !== options.parent_uuid)
              continue;

            agreementsEntities[agreement.uuid] = {
              ...agreement,
              parentId,
              breadcrumb_name:
                insuranceObject?.insurance_object_type ===
                INDEX_OBJECT_TYPES.PROPERTY
                  ? `${indexOfAgreement.name}_${insuranceObject?.name ?? ''}_${agreement.name}`
                  : `${indexOfAgreement.name}_${agreement.registration_plate}_${agreement.name}`,
              directory_name:
                directories.find(
                  (dir) => dir.uuid === insuranceObject?.directory,
                )?.abbreviation || '',
            };
            rootIds.push(agreement.uuid);
          }

          const newAgreementsState: NormalizedState<NormalizedAgreement> = {
            totalCount: rootIds.length,
            entities: agreementsEntities,
            rootIds,
            entityTypeLevel: ExplorerItemType.AGREEMENT,
          };
          // Update store
          set((state) => {
            state.agreementsState = newAgreementsState;
          });

          return newAgreementsState;
        },

        getAgreementsStateSearch: async (agreementsIds: string[]) => {
          const { indexes, getAutoFetchUsers } = useGlobalStore.getState();
          const { getAgreements, getInsuranceObjects, directories } =
            useApiManagerStore.getState();

          const { pagination } = useFiltersStore.getState();

          const filteredIdsForFetch = await useSearchStore
            .getState()
            .searchAgreementUuidsForMyDocuments('search');

          const idsForFetch = agreementsIds
            .filter(
              (id) =>
                !filteredIdsForFetch.length || filteredIdsForFetch.includes(id),
            )
            .slice(
              pagination?.pageIndex! * pagination?.pageSize!,
              pagination?.pageIndex! * pagination?.pageSize! +
                pagination?.pageSize!,
            );

          const fetchedAgreements = await getAgreements(idsForFetch, {
            exclude_archived: false,
          });

          // Extract UUIDs for parallel fetching
          const userUuids = [
            ...new Set(
              fetchedAgreements
                .flatMap((agreement) => [agreement.assignee, agreement.creator])
                .filter((uuid): uuid is string => uuid !== undefined),
            ),
          ];

          const insuranceObjectsUuids = [
            ...new Set(
              fetchedAgreements.flatMap((agreement) => [
                agreement.insurance_object,
              ]),
            ),
          ];

          // Parallel requests for all required data
          const [indexesData, insuranceObjects] = await Promise.all([
            Promise.resolve(indexes), // Use cached indexes from global store
            getInsuranceObjects(insuranceObjectsUuids),
            getAutoFetchUsers(userUuids),
          ]);

          // Preserve ES order using agreementsIds
          const orderIndex: Record<string, number> = {};
          agreementsIds.forEach((id, i) => (orderIndex[id] = i));
          fetchedAgreements.sort((a, b) => {
            const ai = orderIndex[a.uuid];
            const bi = orderIndex[b.uuid];
            const da = ai === undefined ? Number.POSITIVE_INFINITY : ai;
            const db = bi === undefined ? Number.POSITIVE_INFINITY : bi;
            return da - db;
          });

          // Create normalized agreements in preserved order
          const agreementsEntities: Record<string, NormalizedAgreement> = {};
          const rootIds: string[] = [];
          for (const agreement of fetchedAgreements) {
            const indexOfAgreement = indexesData.find(
              (index) => index.uuid === agreement.index,
            );
            if (!indexOfAgreement) continue;

            const insuranceObject = insuranceObjects.find(
              (obj) => obj.uuid === agreement.insurance_object,
            );

            const parentId =
              `${indexOfAgreement.insurance_area}_${agreement.insurance_object}` as `${DirectionType}_${string}`;

            agreementsEntities[agreement.uuid] = {
              ...agreement,
              parentId,
              breadcrumb_name:
                insuranceObject?.insurance_object_type ===
                INDEX_OBJECT_TYPES.PROPERTY
                  ? `${indexOfAgreement.name}_${insuranceObject?.name ?? ''}_${agreement.name}`
                  : `${indexOfAgreement.name}_${agreement.registration_plate}_${agreement.name}`,
              directory_name:
                directories.find(
                  (dir) => dir.uuid === insuranceObject?.directory,
                )?.abbreviation || '',
            };
            rootIds.push(agreement.uuid);
          }

          const newAgreementsState: NormalizedState<NormalizedAgreement> = {
            totalCount: rootIds.length,
            entities: agreementsEntities,
            rootIds,
            entityTypeLevel: ExplorerItemType.AGREEMENT,
          };
          // Update store
          set((state) => {
            state.agreementsState = newAgreementsState;
          });

          return newAgreementsState;
        },

        setAgreementsState: (
          agreementsState: NormalizedState<NormalizedAgreement>,
        ) => {
          set((state) => {
            state.agreementsState = agreementsState;
          });
        },

        updateAgreementInStateByDTO: (agreement: AgreementDTO) => {
          const agreementState = get().agreementsState;
          const newAgreementsState: NormalizedState<NormalizedAgreement> = {
            ...agreementState,
            entities: {
              ...agreementState.entities,
              [agreement.uuid]: {
                ...agreementState.entities[agreement.uuid],
                ...agreement,
              },
            },
          };
          set((state) => {
            state.agreementsState = newAgreementsState;
          });
        },

        setAgreementsUuids: (uuids: string[]) => {
          set((state) => {
            state.agreementsState.rootIds = uuids;
          });
        },

        addAgreement: (agreement: NormalizedAgreement) => {
          set((state) => {
            state.agreementsState.entities[agreement.uuid] = agreement;
            state.agreementsState.rootIds.push(agreement.uuid);
          });
        },

        getInsuranceAreaState: async (
          skip?: number,
          take?: number,
          parent_uuid?: string,
        ) => {
          const { getInsuranceArea } = useApiManagerStore.getState();
          const insuranceAreas = await getInsuranceArea(undefined, {
            take,
            skip,
            parent_uuid,
          });

          const insuranceAreasEntities = insuranceAreas.reduce(
            (acc, insuranceArea) => {
              acc[insuranceArea.uuid] = {
                ...insuranceArea,
                parentId: parent_uuid || insuranceArea.insurance_object,
                breadcrumb_name: insuranceArea.name,
              };
              return acc;
            },
            {} as Record<string, NormalizedInsuranceArea>,
          );

          const insuranceAreasRootIds = [
            ...new Set(Object.keys(insuranceAreasEntities)),
          ];

          const insuranceAreasState: NormalizedState<NormalizedInsuranceArea> =
            {
              entities: insuranceAreasEntities,
              rootIds: insuranceAreasRootIds,
              entityTypeLevel: ExplorerItemType.INSURANCE_AREA,
              totalCount: insuranceAreasRootIds.length,
            };

          set((state) => {
            state.insuranceAreaState = insuranceAreasState;
          });
          return insuranceAreasState;
        },

        setInsuranceAreaState: (
          insuranceAreaState: NormalizedState<NormalizedInsuranceArea>,
        ) => {
          set((state) => {
            state.insuranceAreaState = insuranceAreaState;
          });
        },

        getInsuranceObjectsState: async (
          skip?: number,
          take?: number,
          parent_uuid?: string,
        ) => {
          const { getInsuranceObjects } = useApiManagerStore.getState();

          const insuranceObjects = await getInsuranceObjects(undefined, {
            take,
            skip,
            parent_uuid,
          });

          // Create normalized insurance objects
          const insuranceObjectsEntities = insuranceObjects.reduce(
            (acc, insuranceObject) => {
              acc[insuranceObject.uuid] = {
                ...insuranceObject,
                parentId: insuranceObject.directory,
                breadcrumb_name: insuranceObject.name,
              };
              return acc;
            },
            {} as Record<string, NormalizedInsuranceObject>,
          );

          // Create state with rootIds from server response
          const rootIds = insuranceObjects.map(
            (insuranceObject) => insuranceObject.uuid,
          );

          const newInsuranceObjectsState: NormalizedState<NormalizedInsuranceObject> =
            {
              entities: insuranceObjectsEntities,
              rootIds,
              entityTypeLevel: ExplorerItemType.INSURANCE_OBJECT,
              totalCount: rootIds.length,
            };
          // Update store
          set((state) => {
            state.insuranceObjectsState = newInsuranceObjectsState;
          });

          return newInsuranceObjectsState;
        },

        setInsuranceObjectsUuids: (uuids: string[]) => {
          set((state) => {
            state.insuranceObjectsState.rootIds = uuids;
          });
        },

        setInsuranceObjectsState: (
          insuranceObjectsState: NormalizedState<NormalizedInsuranceObject>,
        ) => {
          set((state) => {
            state.insuranceObjectsState = insuranceObjectsState;
          });
        },

        getFilesState: async (
          agreementUuid?: string,
          skip?: number,
          take?: number,
        ) => {
          const { getAutoFetchFiles } = useApiManagerStore.getState();

          const files = await getAutoFetchFiles(undefined, {
            skip,
            take,
            parent_uuid: agreementUuid,
          });

          const { filesState } = get();

          // If this is a fresh load (skip = 0), reset the state
          const isInitialLoad = !skip || skip === 0;

          if (isInitialLoad) {
            // Fresh load - replace everything
            const filesEntities = files.reduce(
              (acc, file) => {
                acc[file.uuid] = {
                  ...file,
                  parentId: file.agreement,
                  agreement: file.agreement,
                  breadcrumb_name: file.name,
                };
                return acc;
              },
              {} as Record<string, NormalizedFile>,
            );

            const newFilesState: NormalizedState<NormalizedFile> = {
              entities: filesEntities,
              rootIds: files.map((file) => file.uuid),
              entityTypeLevel: ExplorerItemType.FILE,
              totalCount: files.length,
            };

            set((state) => {
              state.filesState = newFilesState;
            });
            return newFilesState;
          } else {
            // Incremental load - append new files, avoid duplicates
            const existingFileIds = new Set(filesState.rootIds);
            const newFiles = files.filter(
              (file) => !existingFileIds.has(file.uuid),
            );

            if (newFiles.length === 0) {
              // No new files, return current state
              return filesState;
            }

            const newFilesEntities = newFiles.reduce(
              (acc, file) => {
                acc[file.uuid] = {
                  ...file,
                  parentId: file.agreement,
                  agreement: file.agreement,
                  breadcrumb_name: file.name,
                };
                return acc;
              },
              {} as Record<string, NormalizedFile>,
            );

            const newFileIds = newFiles.map((file) => file.uuid);

            const updatedFilesState: NormalizedState<NormalizedFile> = {
              entities: {
                ...filesState.entities,
                ...newFilesEntities,
              },
              rootIds: [...filesState.rootIds, ...newFileIds],
              entityTypeLevel: ExplorerItemType.FILE,
              totalCount: Math.max(
                filesState.totalCount,
                filesState.rootIds.length + newFileIds.length,
              ),
            };

            set((state) => {
              state.filesState = updatedFilesState;
            });
            return updatedFilesState;
          }
        },

        // Helper methods with auto-fetching
        getStateByType: async (
          type: ExplorerItemType,
          parentUuid?: string,
          pagination?: { pageIndex: number; pageSize: number },
          exclude_archived?: boolean,
        ) => {
          const skip = pagination
            ? pagination.pageIndex * pagination.pageSize
            : 0;
          const take = pagination ? pagination.pageSize : 20;

          const state = await (async () => {
            switch (type) {
              case ExplorerItemType.DIRECTORY:
                return await get().getDirectoriesState(skip, take);
              case ExplorerItemType.INSURANCE_OBJECT:
                return await get().getInsuranceObjectsState(
                  skip,
                  take,
                  parentUuid,
                );
              case ExplorerItemType.INSURANCE_AREA:
                return await get().getInsuranceAreaState(
                  skip,
                  take,
                  parentUuid,
                );
              case ExplorerItemType.AGREEMENT:
                return await get().getAgreementsState({
                  parent_uuid: parentUuid as InsuranceAreaDTO['uuid'],
                  exclude_archived,
                });
              case ExplorerItemType.FILE:
                return await get().getFilesState(parentUuid, skip, take);
            }
          })();

          return state;
        },

        getEntityByUuid: async (
          type: ExplorerItemType,
          currentUUID: string,
        ): Promise<ExplorerItem | null> => {
          let entity: ExplorerItem | null = null;

          try {
            switch (type) {
              case ExplorerItemType.AGREEMENT: {
                const { getAgreements } = useApiManagerStore.getState();
                const agreements = await getAgreements([currentUUID]);
                if (agreements.length > 0) {
                  const agreement = agreements[0];
                  const { indexes, getAutoFetchUsers } =
                    useGlobalStore.getState();
                  const { getInsuranceObjects, directories } =
                    useApiManagerStore.getState();

                  const [indexesData, insuranceObjects] = await Promise.all([
                    Promise.resolve(indexes), // 🎯 Use cached indexes from global store
                    getInsuranceObjects([agreement.insurance_object]),
                    getAutoFetchUsers(
                      [agreement.assignee, agreement.creator].filter(
                        (uuid): uuid is string => uuid !== undefined,
                      ),
                    ),
                  ]);

                  const indexOfAgreement = indexesData.find(
                    (index) => index.uuid === agreement.index,
                  );

                  const insuranceObject = insuranceObjects.find(
                    (obj) => obj.uuid === agreement.insurance_object,
                  );

                  if (indexOfAgreement) {
                    const parentId =
                      `${indexOfAgreement.insurance_area}_${agreement.insurance_object}` as `${DirectionType}_${string}`;

                    entity = {
                      ...agreement,
                      parentId,
                      breadcrumb_name:
                        insuranceObject?.insurance_object_type ===
                        INDEX_OBJECT_TYPES.PROPERTY
                          ? `${indexOfAgreement.name}_${insuranceObject.name}_${agreement.name}`
                          : `${indexOfAgreement.name}_${agreement.registration_plate}_${agreement.name}`,
                      directory_name:
                        directories.find(
                          (dir) => dir.uuid === insuranceObject?.directory,
                        )?.abbreviation || '',
                    };

                    // Update the store with the fetched entity
                    set((state) => {
                      state.agreementsState.entities[currentUUID] =
                        entity as NormalizedAgreement;
                      state.agreementsState.rootIds.push(currentUUID);
                    });
                  }
                }
                break;
              }
              case ExplorerItemType.INSURANCE_OBJECT: {
                const { getInsuranceObjects } = useApiManagerStore.getState();
                const insuranceObjects = await getInsuranceObjects([
                  currentUUID,
                ]);
                if (insuranceObjects.length > 0) {
                  const obj = insuranceObjects[0];
                  entity = {
                    ...obj,
                    parentId: obj.directory,
                    breadcrumb_name: obj.name,
                  };

                  // Update the store with the fetched entity
                  set((state) => {
                    state.insuranceObjectsState.entities[currentUUID] =
                      entity as NormalizedInsuranceObject;
                    state.insuranceObjectsState.rootIds.push(currentUUID);
                  });
                }
                break;
              }
              case ExplorerItemType.INSURANCE_AREA: {
                const { getInsuranceArea } = useApiManagerStore.getState();
                const insuranceArea = await getInsuranceArea(undefined, {
                  parent_uuid: getInsuranceObjectIdFromInsuranceAreaId(
                    currentUUID as InsuranceAreaDTO['uuid'],
                  ),
                });
                const currentIA = insuranceArea.find(
                  (area) => area.uuid === currentUUID,
                );
                if (currentIA) {
                  const area = currentIA;
                  entity = {
                    ...area,
                    parentId: area.insurance_object,
                    breadcrumb_name: area.name,
                  };

                  // Update the store with the fetched entity
                  set((state) => {
                    state.insuranceAreaState.entities[currentUUID] =
                      entity as NormalizedInsuranceArea;
                    state.insuranceAreaState.rootIds.push(currentUUID);
                  });
                }
                break;
              }
              case ExplorerItemType.DIRECTORY: {
                const updatedState = get().directoriesState;
                entity = updatedState.entities[currentUUID];
                break;
              }
              default: {
                return null;
              }
            }
          } catch (error) {
            console.error(`Failed to auto-fetch entity ${currentUUID}:`, error);
            return null;
          }

          return entity || null;
        },

        getBreadcrumbItems: async (
          state_entityTypeLevel: ExplorerItemType,
          parentUuid: string,
        ) => {
          const directoriesState = await get().getDirectoriesState();

          const rootItem: BreadcrumbItemType = {
            name: 'Дирекція',
            id: ROOT_UUID,
            type: ExplorerItemType.DIRECTORY,
          };

          const buildBreadcrumbs = async (
            currentType: ExplorerItemType,
            parentUuid: string,
          ): Promise<BreadcrumbItemType[]> => {
            const parentType = currentType - 1;
            if (
              parentUuid === null ||
              parentType < ExplorerItemType.DIRECTORY
            ) {
              return [rootItem];
            }

            const entity = await get().getEntityByUuid(parentType, parentUuid);

            if (!entity) {
              return [rootItem];
            }

            const currentItem = {
              name: entity.breadcrumb_name,
              id: entity.uuid,
              type: currentType,
              parentId: entity.parentId,
            };

            if (!entity.parentId) {
              return [rootItem, currentItem];
            }

            const parentBreadcrumbs = await buildBreadcrumbs(
              parentType,
              entity.parentId,
            );
            return [...parentBreadcrumbs, currentItem];
          };

          if (parentUuid === '') {
            return [
              {
                name: AGREEMENT_ROOT_NAME,
                id: '',
                type: ExplorerItemType.AGREEMENT,
              },
            ];
          }

          const breadcrumbsArray = await buildBreadcrumbs(
            state_entityTypeLevel,
            parentUuid,
          );

          if (directoriesState.rootIds.length <= 1) {
            breadcrumbsArray.shift();
          }

          return breadcrumbsArray;
        },

        // Clear files cache for a specific agreement
        clearFilesCache: (agreementUuid?: string) => {
          set((state) => {
            if (agreementUuid) {
              // Clear files for specific agreement
              const filteredEntities = Object.fromEntries(
                Object.entries(state.filesState.entities).filter(
                  ([_, file]) => file.agreement !== agreementUuid,
                ),
              );
              const filteredRootIds = state.filesState.rootIds.filter(
                (id) =>
                  state.filesState.entities[id]?.agreement !== agreementUuid,
              );

              state.filesState = {
                ...state.filesState,
                entities: filteredEntities,
                rootIds: filteredRootIds,
              };
            } else {
              // Clear all files
              state.filesState = {
                entities: {},
                rootIds: [],
                entityTypeLevel: ExplorerItemType.FILE,
                totalCount: 0,
              };
            }
          });
        },

        // Remove a specific file from cache
        removeFileFromCache: (fileUuid: string) => {
          set((state) => {
            const { [fileUuid]: removedFile, ...remainingEntities } =
              state.filesState.entities;
            state.filesState = {
              ...state.filesState,
              entities: remainingEntities,
              rootIds: state.filesState.rootIds.filter((id) => id !== fileUuid),
              totalCount: Math.max(0, state.filesState.totalCount - 1),
            };
          });
        },
      }),
    ),
    {
      name: 'file-explorer-storage', // name for the storage key
      partialize: (state) => {
        return {
          visibleColumns: state.visibleColumns,
          mediaViewMode: state.mediaViewMode,
        };
      },
    },
  ),
);
